// ============================================================
// A PLATE'S TRACKS (plan body-map S1): what the bench animated with SMIL - a
// filter's seed, an offset, a band's height - as a looping keyframe track
// that the plate's one clock samples each frame. SMIL is not used: it ignores
// the motion setting and fake timers (ADR 2026-10-09). A track also names its
// STILL value, the form it holds when the screen plays no motion.
//
// Pure. Zero React dependency.
// ============================================================

export interface Track {
  /** The attribute it sets. */
  attr: string;
  /** Keyframes, evenly spaced over the duration; it loops. */
  values: number[];
  /** Seconds per loop. */
  dur: number;
  /** linear (default), discrete (each value holds its share), or ease (slow at every keyframe). */
  mode?: 'linear' | 'discrete' | 'ease';
  /** What it holds with motion off; the first value when absent. */
  still?: number;
  /** Seconds it runs ahead (SMIL's negative `begin`): siblings out of step, never moving as one. */
  offset?: number;
}

/** A looping track: an attribute through its keyframes every `dur` seconds (`extra`: its mode, still, offset). */
export const loop = (attr: string, values: number[], dur: number, extra: Partial<Track> = {}): Track => ({
  attr,
  values,
  dur,
  ...extra,
});

/** The fractional part: where in its loop a phase is. */
export const frac = (x: number) => x - Math.floor(x);

const smooth = (x: number) => x * x * (3 - 2 * x);

/** The track's value `t` seconds in. */
export function sampleTrack(track: Track, t: number): number {
  const { values, dur, mode = 'linear', offset = 0 } = track;
  if (values.length < 2 || !(dur > 0)) return values[0];
  const time = t + offset;
  const phase = (((time % dur) + dur) % dur) / dur;
  if (mode === 'discrete') return values[Math.floor(phase * values.length)];
  const at = phase * (values.length - 1);
  const i = Math.floor(at);
  const f = mode === 'ease' ? smooth(at - i) : at - i;
  return values[i] + (values[i + 1] - values[i]) * f;
}

/** The value a track holds with motion off. */
export const stillOf = (track: Track) => track.still ?? track.values[0];

/**
 * A number as an attribute's value: five significant digits - a fixed number of decimals would step a small value
 * (a noise frequency of 0.002 to 0.01) in visible jumps, and full precision writes 17 characters for nothing.
 */
export const attrNumber = (v: number) => String(Number(v.toPrecision(5)));
